import React, { useState } from 'react';
import {
// Planning icons
Lightbulb, Search, CheckCircle, Layout, Users, FileCode,
// Development icons
Code2Icon, GlobeIcon, SmartphoneIcon, LinkIcon, CloudIcon,
// Support icons
DatabaseIcon, ServerIcon, ActivityIcon, HeadphonesIcon, ShieldIcon,
// New post-launch icons
RefreshCwIcon, ZapIcon, PlusCircleIcon, ClockIcon,
// SEECRYPT icon
LockIcon } from 'lucide-react';
export const UnifiedServicesSection = () => {
  const [activeTab, setActiveTab] = useState('planning');
  const serviceCategories = [{
    id: 'planning',
    title: 'Strategic Planning',
    subtitle: 'Before a Single Line of Code',
    description: 'The crucial first phase where we help you validate your idea, define the scope, and plan for success.',
    color: 'bg-gray-100',
    textColor: 'text-gray-800'
  }, {
    id: 'development',
    title: 'Core Development',
    subtitle: 'The Actual Building',
    description: 'The heart of what we do: writing the code and building the application that brings your vision to life.',
    color: 'bg-black',
    textColor: 'text-white'
  }, {
    id: 'support',
    title: 'Post-Launch & Support',
    subtitle: 'Ensuring Long-Term Success',
    description: "We don't disappear after launch. Our ongoing services ensure your software remains secure, efficient, and aligned with your evolving business needs.",
    color: 'bg-gray-900',
    textColor: 'text-white'
  }];
  const planningServices = [{
    icon: <Lightbulb className="h-12 w-12 mb-4" data-id="element-229" />,
    title: 'Discovery & Strategy Workshops',
    description: 'Collaborative sessions to understand your business goals, target audience, and challenges. The output is a clear project vision and roadmap.'
  }, {
    icon: <Search className="h-12 w-12 mb-4" data-id="element-230" />,
    title: 'Product & Market Research',
    description: 'Analyzing the competition, identifying market needs, and helping to position your product effectively in your target market.'
  }, {
    icon: <CheckCircle className="h-12 w-12 mb-4" data-id="element-231" />,
    title: 'Feasibility & Technical Analysis',
    description: 'Determining if your idea is technically possible within your budget and timeline, suggesting the optimal technology stack for your needs.'
  }, {
    icon: <Users className="h-12 w-12 mb-4" data-id="element-232" />,
    title: 'UX (User Experience) Design',
    description: 'Creating user personas, mapping user journeys, and designing intuitive workflows that make your software a joy to use.'
  }, {
    icon: <Layout className="h-12 w-12 mb-4" data-id="element-233" />,
    title: 'UI (User Interface) Design',
    description: 'Crafting beautiful, responsive interfaces with wireframes, mockups, and interactive prototypes that show exactly how your app will look and feel.'
  }, {
    icon: <FileCode className="h-12 w-12 mb-4" data-id="element-234" />,
    title: 'MVP (Minimum Viable Product) Scoping',
    description: 'Defining the core features needed to launch quickly, test the market, and gather feedback before investing in a full-featured version.'
  }];
  const developmentServices = [{
    icon: <Code2Icon className="h-12 w-12 mb-4" data-id="element-235" />,
    title: 'Custom Software Development',
    description: 'Building unique software solutions from scratch, tailored specifically to your business processes and needs.',
    subItems: ['Business Process Automation', 'Custom CRM & ERP Systems', 'Workflow Management Tools']
  }, {
    icon: <GlobeIcon className="h-12 w-12 mb-4" data-id="element-236" />,
    title: 'Web Application Development',
    description: 'Creating sophisticated web applications with advanced features beyond what off-the-shelf solutions can offer.',
    subItems: ['SaaS (Software as a Service) Products', 'Customer Portals & Dashboards', 'Internal Business Tools', 'E-commerce Platforms']
  }, {
    icon: <SmartphoneIcon className="h-12 w-12 mb-4" data-id="element-237" />,
    title: 'Mobile App Development',
    description: 'Building high-performance mobile applications for iOS and Android that engage users and drive business value.',
    subItems: ['Native Apps (iOS & Android)', 'Cross-Platform Apps (React Native, Flutter)']
  }, {
    icon: <LinkIcon className="h-12 w-12 mb-4" data-id="element-238" />,
    title: 'API Development & Integration',
    description: 'Creating APIs that allow your software to communicate with other systems like payment gateways, mapping services, or your internal databases.',
    subItems: ['RESTful API Development', 'Third-party Service Integration', 'Legacy System Integration']
  }, {
    icon: <CloudIcon className="h-12 w-12 mb-4" data-id="element-239" />,
    title: 'Cloud & DevOps Services',
    description: 'Setting up scalable cloud infrastructure with automated processes for building, testing, and deploying your software.',
    subItems: ['Cloud Infrastructure (AWS, GCP, Azure)', 'DevOps & CI/CD Pipelines', 'Containerization & Orchestration']
  }];
  const supportServices = [{
    icon: <LockIcon className="h-12 w-12 mb-4" data-id="element-240" />,
    title: 'SEECRYPT Secure Communications',
    description: 'Military-grade encryption for secure phone calls, video calls, conference calls, instant messaging, and large file transfers, with Enterprise IT Management and Metadata control.',
    subItems: ['Encrypted Voice & Video Calls', 'Secure Messaging & File Transfers', 'Seecrypt Voice Gateway for PBX Integration', 'Enterprise IT Management', 'VoIP Cost Savings with Military-Grade Security']
  }, {
    icon: <HeadphonesIcon className="h-12 w-12 mb-4" data-id="element-241" />,
    title: 'Support & Maintenance',
    description: 'Ongoing technical support to ensure your application runs smoothly and all user issues are addressed promptly.',
    subItems: ['Bug Fixes & Issue Resolution', 'User Support & Help Desk', 'Monthly Maintenance Retainers', 'Regular Security Updates']
  }, {
    icon: <RefreshCwIcon className="h-12 w-12 mb-4" data-id="element-242" />,
    title: 'Software Modernization & Legacy Migration',
    description: 'Transforming outdated systems into modern, efficient applications without disrupting your business operations.',
    subItems: ['Legacy System Assessment', 'Data Migration & Preservation', 'Code Refactoring & Modernization', 'Technology Stack Upgrades']
  }, {
    icon: <ZapIcon className="h-12 w-12 mb-4" data-id="element-243" />,
    title: 'Performance Optimization',
    description: "Analyzing and improving your application's speed, responsiveness, and efficiency to enhance user experience.",
    subItems: ['Load Time Optimization', 'Database Query Optimization', 'Memory Usage & CPU Optimization', 'Scalability Improvements']
  }, {
    icon: <PlusCircleIcon className="h-12 w-12 mb-4" data-id="element-244" />,
    title: 'Feature Enhancements & Iteration',
    description: 'Evolving your product over time with new features and improvements based on user feedback and changing business needs.',
    subItems: ['Feature Planning & Roadmapping', 'User Feedback Analysis', 'Continuous Improvement Cycles', 'Version Upgrades & Releases']
  }, {
    icon: <ClockIcon className="h-12 w-12 mb-4" data-id="element-245" />,
    title: 'Proactive Monitoring',
    description: 'Continuous monitoring of your application to identify and resolve issues before they impact your users or business.',
    subItems: ['Uptime & Performance Monitoring', 'Error Tracking & Alerts', 'Capacity Planning', 'Preventative Maintenance']
  }, {
    icon: <DatabaseIcon className="h-12 w-12 mb-4" data-id="element-246" />,
    title: 'Data Management & Analytics',
    description: 'Ongoing management of your data assets with solutions to extract actionable business insights.',
    subItems: ['Data Warehousing & Storage', 'Business Intelligence Dashboards', 'Analytics Implementation', 'Data Backup & Recovery']
  }, {
    icon: <ShieldIcon className="h-12 w-12 mb-4" data-id="element-247" />,
    title: 'Security & Compliance',
    description: 'Continuous security monitoring and updates to protect your application against evolving threats.',
    subItems: ['Regular Security Audits', 'Vulnerability Assessments', 'Compliance Updates (GDPR, HIPAA, etc.)', 'Security Patch Management']
  }];
  const getActiveServices = () => {
    switch (activeTab) {
      case 'planning':
        return planningServices;
      case 'development':
        return developmentServices;
      case 'support':
        return supportServices;
      default:
        return planningServices;
    }
  };
  const activeCategory = serviceCategories.find(cat => cat.id === activeTab);
  return <section id="services" className={`w-full ${activeCategory?.color} ${activeCategory?.textColor} py-20 transition-colors duration-500`} data-id="element-248">
      <div className="container mx-auto px-4" data-id="element-249">
        <div className="text-center mb-12" data-id="element-250">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-251">Our Services</h2>
          <div className="w-24 h-1 bg-red-600 mx-auto" data-id="element-252"></div>
          <p className="mt-6 max-w-3xl mx-auto" data-id="element-253">
            We deliver comprehensive technology solutions throughout the entire
            software development lifecycle.
          </p>
        </div>
        {/* Service Category Tabs */}
        <div className="flex flex-col md:flex-row justify-center mb-12 gap-4" data-id="element-254">
          {serviceCategories.map(category => <button key={category.id} onClick={() => setActiveTab(category.id)} className={`px-6 py-3 rounded-lg font-medium transition-colors 
                ${activeTab === category.id ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`} data-id="element-255">
              {category.title}
            </button>)}
        </div>
        {/* Active Category Header */}
        <div className="text-center mb-12" data-id="element-256">
          <h3 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-257">
            {activeCategory?.title}
          </h3>
          <p className={`${activeTab === 'planning' ? 'text-red-600' : 'text-red-500'} font-medium italic mb-2`} data-id="element-258">
            {activeCategory?.subtitle}
          </p>
          <p className={`${activeTab === 'planning' ? 'text-gray-600' : 'text-gray-300'} max-w-2xl mx-auto`} data-id="element-259">
            {activeCategory?.description}
          </p>
        </div>
        {/* Development Image - Only shown when development tab is active */}
        {activeTab === 'development' && <div className="mb-16" data-id="element-260">
            <div className="rounded-lg overflow-hidden shadow-xl w-full" data-id="element-261">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vxmDYHQq7Cgu4Zs5aiKuwi/vecteezy_diverse-business-colleagues-reviewing-project-details-to_49382488.jpg" alt="Development team collaborating on a project" className="w-full h-auto filter grayscale contrast-125 brightness-90" style={{
            objectFit: 'cover'
          }} data-id="element-262" />
            </div>
            <p className="text-center text-gray-400 mt-4 text-sm italic" data-id="element-263">
              Our development team collaborating on innovative solutions
            </p>
          </div>}
        {/* Post-Launch Image - Only shown when support tab is active */}
        {activeTab === 'support' && <div className="mb-16" data-id="element-264">
            <div className="rounded-lg overflow-hidden shadow-xl w-full" data-id="element-265">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vqcEHzvTn6DGGkdpa3bBuC/vecteezy_the-businessman-holding-a-rocket-symbolizing-innovation-and_66578442.jpg" alt="Business innovation represented by a rocket launch" className="w-full h-auto filter grayscale contrast-125 brightness-90" style={{
            objectFit: 'cover'
          }} data-id="element-266" />
            </div>
            <p className="text-center text-gray-400 mt-4 text-sm italic" data-id="element-267">
              Launching your business to new heights with our post-launch
              support
            </p>
          </div>}
        {/* Services List */}
        <div className={`${activeTab === 'planning' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-8' : 'space-y-8'}`} data-id="element-268">
          {getActiveServices().map((service, index) => <div key={index} className={`${activeTab === 'planning' ? 'bg-white p-8 rounded-lg shadow-lg transition-transform hover:transform hover:scale-105' : 'bg-gray-900 p-8 rounded-lg transition-all hover:bg-gray-800'}`} data-id="element-269">
              <div className={`${activeTab === 'planning' ? 'flex flex-col items-center text-center text-red-600' : 'flex flex-col md:flex-row'}`} data-id="element-270">
                <div className={`${activeTab === 'planning' ? '' : 'text-red-600 md:mr-6 flex-shrink-0 flex md:block justify-center'}`} data-id="element-271">
                  {service.icon}
                </div>
                <div className={activeTab === 'planning' ? 'text-center' : ''} data-id="element-272">
                  <h3 className={`text-xl font-bold mb-3 ${activeTab === 'planning' ? 'text-gray-800' : 'text-red-600'}`} data-id="element-273">
                    {service.title}
                  </h3>
                  <p className={`${activeTab === 'planning' ? 'text-gray-600' : 'text-gray-400'} mb-4`} data-id="element-274">
                    {service.description}
                  </p>
                  {service.subItems && <ul className={`${activeTab === 'planning' ? 'mt-4 space-y-2' : 'grid md:grid-cols-2 gap-2 mt-4'}`} data-id="element-275">
                      {service.subItems.map((item, idx) => <li key={idx} className={`flex items-center ${activeTab === 'planning' ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-276">
                          <span className="h-1.5 w-1.5 bg-red-600 rounded-full mr-2" data-id="element-277"></span>
                          {item}
                        </li>)}
                    </ul>}
                </div>
              </div>
            </div>)}
        </div>
        {/* Call to Action */}
        <div className="mt-16 bg-red-600 rounded-lg p-8 text-center text-white" data-id="element-278">
          <h3 className="text-2xl font-bold mb-4" data-id="element-279">
            Ready to Transform Your Business with Binary Solutions 101010?
          </h3>
          <p className="mb-6" data-id="element-280">
            Join our satisfied clients who have enhanced their operations with
            our cutting-edge solutions.
          </p>
        </div>
      </div>
    </section>;
};